<template>
    <div>
        <vehicle-lookup-form 
            ref="lookup" 
            :title="lookupTitle"
            @success="handleLookupSuccess"
            @failure="$emit('lookup-failure', $event)"
            :is-anpr-available="true"
            :optional-mileage="optionalMileage"
        ></vehicle-lookup-form>

        <vehicle-definition-modal 
            ref="definition" 
            @save="handleVehicleDefinition" 
            @change-basic-description="showLookup"
            :from-partial-lookup="isPartialResponse"
            :auto-close-on-save="false"
        ></vehicle-definition-modal>

        <vehicle-instance-modal 
            ref="instance" 
            :is-edit-mode="false"
            @skipped="handleAdditionalDetailsSaved"
            @saved="handleAdditionalDetailsSaved"
        ></vehicle-instance-modal>

        <vehicle-added-modal 
            ref="added" 
            :loaded="loaded" 
            v-if="this.identity" 
            :show-buttons="!shouldHideConfirmationButtons"
            @hide="reset"
            @add-another="addAnother" 
        ></vehicle-added-modal>
    </div>
</template>

<script>
    import VehicleInstanceModal from './VehicleInstanceModal';
    import VehicleAddedModal from './VehicleAddedModal';
    import { VehicleIdentityHelpers } from '../../mixins/VehicleHelpers';
    import { VehicleIdentitySchema, VehicleInstanceResource, VehicleInstanceMarkets } from '../../../classes/Api/Vehicle';
    import { mapState, mapMutations, mapActions } from 'vuex'
    import Moment from "moment";
    import {StockListingAssetClass} from "../../../../../api-clients/ts";
    
    export default {
        props: {
            shouldCreateInstance: {
                type: Boolean,
                default() {
                    return true;
                }
            },
            isAlien: {
                type: Boolean,
                default() {
                    return false;
                }
            },
            shouldShowConfirmation: {
                type: Boolean,
                default() {
                    return true;
                }
            },
            shouldHideConfirmationButtons: {
                type: Boolean,
                default() {
                    return false;
                }
            },
            shouldShowMoreDetails: {
                type: Boolean,
                default() {
                    return false;
                }
            },
            overrideLookupTitle: {
               type: String,
               required: false
            },
            optionalMileage: {
                type: Boolean,
                default() {
                    return window.marque.ui.tweaks.allow_mileage_estimates
                }
            },
        },
        mixins: [VehicleIdentityHelpers],
        components: {
            VehicleInstanceModal,
            VehicleAddedModal,
        },
        data() {
            return {
                loaded: false,
                VehicleInstanceMarkets,
                additionalDetail: {},
            }
        },
        mounted() {
            // @deprecated Use open()
            // A listener to proxy calls for the initial lookup 
            // screen to the lookup component.
            this.$parent.$on('add-vehicle-show', () => {
                this.open();
            });

            // Called by the identity helper to gather more 
            // information about the vehicle.
            this.$on('show-definition-modal', () => {
                this.$refs['lookup'].$once('hide', () => {
                    this.$refs['definition'].open()
                });

                this.$refs['lookup'].close();
            });

            // Hide the loaders on the definition modal.
            this.$on('vehicle-creation-failed', (e) => {
                this.$refs['definition'].stopLoading()
            })

            // Called by the identity helpers on successful 
            // vehicle creation.
            if (this.shouldCreateInstance) {
                this.$on('vehicle-created', (data) => {
                    let instance = {
                        vehicle_id: data[1].id,
                        market: this.isAlien ? VehicleInstanceMarkets.NONE : VehicleInstanceMarkets.RETAIL,
                        asset_class: this.isAlien ? StockListingAssetClass.None : StockListingAssetClass.Stock,
                        location_id: this.isAlien ? null : window.marque.ui.location.id,
                        delivery_on: window.marque.ui.tweaks.auto_delivery_date ? Moment() : null,
                        owners: data[0].owners
                    };

                    let promise;

                    // If we are creating trying to ID a non-stock car, when we have 
                    // an existing vehicle with any status within the system, we should return it.
                    if (this.isAlien && data[1].current_instance) {
                        promise = new Promise((resolve, reject) => {
                            this.setInstance(data[1].current_instance)
                            resolve(data[1].current_instance)
                        });
                    } 

                    // Otherwise we just create the instance as usual.
                    else {
                        promise = this.createInstance(instance);
                    }

                    // Create the instance and add it to the store.
                    promise.then(instance => {
                        this.$emit('base-instance-created', this.instance);

                        if (this.shouldShowMoreDetails) {
                            let openModal = this.$refs['lookup'].isOpen ? 'lookup' : 'definition';

                            this.$refs[openModal].$once('hide', () => {
                                this.$refs['instance'].open(this.additionalDetail);
                            });

                            this.$refs[openModal].close();
                        } else {
                            this.showFinish();
                        }
                    }, e => {
                        this.$refs['lookup'].isLoading = false
                        this.$emit('stocklisting-creation-failure')
                    });
                })
            }
        },
        computed: {
            ...mapState({ 
                instance: state => state.instance.instance,
            }),
            lookupTitle() {
                if (this.overrideLookupTitle) {
                    return this.overrideLookupTitle
                }

                return this.isAlien ? 'Add Non-Stock Vehicle' : 'Add Vehicle To Stock'
            },
        },
        methods: {
            ...mapMutations({ 'setInstance': 'instance/set' }),
            ...mapActions({ 'createInstance': 'instance/create' }),
            addAnother() {
                this.reset();
                this.showLookup();
            },
            open() {
                this.$refs['lookup'].open();
            },
            openWithDefaults(vrm, mileage, additionalDetail = {}) {
                this.additionalDetail = additionalDetail

                this.$refs['lookup'].openWithDefaults(vrm, mileage)
            },
            close() {
                this.$refs['lookup'].close();
                this.reset();
            },
            reset() {
                this.loaded = false;
                this.lookupCompleted = false;
            },
            showLookup() {
                this.$refs['lookup'].open()
            },
            showLoading() {
                this.$refs['added'].open();
            },
            hideLoading() {
                this.$refs['added'].close();
            },
            handleAdditionalDetailsSaved() {
                this.$refs['instance'].$once('hide', () => {
                    this.showFinish();
                });

                this.$refs['instance'].close();
            },
            showFinish() {
                if (this.shouldShowConfirmation) {
                    this.showLoading();
                
                    this.$refs['added'].setInstance(this.instance);
                }

                this.$emit('instance-created', this.instance);

                setTimeout(() => { 
                    this.loaded = true;
                }, 1000);
            }
        }
    }
</script>